import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import GiftPreview from './GiftPreview';

const MESSAGES = {
  cards: {
    daughter: {
      front: 'To My Beautiful Daughter',
      inside: 'There are not enough words in the world to tell you how much you mean to me. You are my greatest joy, my proudest moment, and the softest part of my heart. Whenever you read this, I hope you feel held — supported, loved, and never, ever alone.'
    },
    son: {
      front: 'To My Special Son',
      inside: 'No matter where life takes you, I want you to carry this with you — I am proud of who you are. You hold more strength, more goodness, and more light than you realise. And whatever comes, my love walks beside you, always.'
    },
    granddaughter: {
      front: 'To My Cherished Granddaughter',
      inside: 'From the very first moment I held you, my heart softened in a way it never had before. You are a quiet miracle, a thread of joy running through our family. Whenever you open this, know that you are adored, supported, and loved beyond every mile and every year.'
    },
    grandson: {
      front: 'To My Cherished Grandson',
      inside: 'You came into our lives and made everything brighter. I hope you always remember how deeply you are loved — not for what you do, but for exactly who you are. Lean on these words whenever the world feels heavy. My love is always here.'
    },
    mum: {
      front: 'To My Wonderful Mum',
      inside: 'Some of the most important words I have ever needed to say to you, I have kept quietly in my heart. Thank you — for the patience, the love, the safe place you gave me to grow. I hope every time you read this, you feel how loved, how appreciated, and how deeply treasured you are.'
    },
    dad: {
      front: 'To My Wonderful Dad',
      inside: 'Thank you — for every steady hand, every quiet lesson, every time you showed up when it mattered most. I carry so much of who you are inside of me. I hope this card reminds you that you are loved, admired, and never taken for granted.'
    },
    neutral: {
      front: 'To Someone I Love',
      inside: 'Some people change your life just by being in it — and you are one of those people. I hope this card finds you on a soft day, and on a hard one, I hope it reminds you: you are seen, you are supported, and you are deeply, endlessly loved.'
    },
  },
  letters: {
    daughter: {
      front: 'To My Beautiful Daughter',
      inside: 'If I could give you one thing, it would be the ability to see yourself through my eyes. Then you would know how extraordinary you are — how gentle, how brave, how full of light.\n\nThis letter holds my voice, my words, and a piece of my heart — so whenever you need to feel close to me, you can simply open it, and I will be there.\n\nOn the hard days, let this be your reminder: you are braver than you believe, stronger than you feel, and loved more than you will ever know.\n\nAlways and forever — I am here.'
    },
    son: {
      front: 'To My Special Son',
      inside: 'There will be days when the world feels heavy. Days when you wonder if you are enough, if you are doing the right thing, if anyone truly understands. On those days, I need you to return to this letter.\n\nBecause you are more than enough. You always have been. You carry kindness, strength, and a heart that the world needs.\n\nThis holds my voice, my words, and a reminder that wherever you go, my love goes with you. You are never walking alone.\n\nI am proud of who you are — today, tomorrow, and every day in between.'
    },
    granddaughter: {
      front: 'To My Cherished Granddaughter',
      inside: 'There is a kind of love that only arrives with a granddaughter — quiet, deep, and endlessly proud. That is the love I carry for you.\n\nI wanted to leave you something you could return to — a place where my voice still lives, my words still hold you, and my love is only ever a page away.\n\nWhen the world feels uncertain, when you are growing into someone new, when you simply need to be reminded of where you came from — return to these words.\n\nYou are adored. You are supported. You are never alone.'
    },
    grandson: {
      front: 'To My Cherished Grandson',
      inside: 'From the moment you came into this world, you changed me. You reminded me that life is still full of hope, still full of beginnings, still full of love worth passing on.\n\nThis letter is my way of staying close — through every step, every season, every door you open. Whenever you need a steady voice, a quiet reminder, or a place to feel held, return to these pages.\n\nBe brave. Be kind. Be yourself — fully.\n\nMy love is with you, always.'
    },
    mum: {
      front: 'To My Wonderful Mum',
      inside: 'There are not enough quiet afternoons, not enough cups of tea, not enough long conversations to tell you everything I want to say. So I am putting it here, where it will last.\n\nThank you for the love that shaped me. Thank you for the patience you gave when I did not deserve it. Thank you for being the softest place I ever landed.\n\nI hope when you read this, you feel seen. Deeply. Completely. Loved — not only for everything you have done, but for exactly who you are.\n\nYou matter more than you know.'
    },
    dad: {
      front: 'To My Wonderful Dad',
      inside: 'So many of the important things you taught me were never spoken aloud. They were in the way you showed up. The way you stayed. The way you quietly carried so much, so that I could carry a little less.\n\nI want you to know: none of it went unnoticed. Every lesson, every sacrifice, every moment you believed in me — it all lives inside me.\n\nThis letter is my thank you. For your strength, your steadiness, your love. I hope, when you open it, you feel how deeply loved, respected, and admired you are.\n\nAlways.'
    },
    neutral: {
      front: 'To Someone I Love',
      inside: 'I wanted to give you something that would last — something you could return to whenever you needed to hear my voice, feel my words, or simply be reminded that you are not alone.\n\nThis letter is that place.\n\nIt holds everything I wish I could say in person, and everything I hope you already know — that you are loved, you are valued, and you matter, deeply, to me.\n\nOn soft days and hard ones, return to these words. I am here. I always will be.'
    },
  },
  journals: {
    daughter: {
      front: 'For My Beautiful Daughter',
      inside: 'This journal is yours — a space to write, to feel, and to hold onto the things that matter.\n\nOn the days when the world is kind, write it down. On the days when it is not, return to these pages and remember: you are extraordinary, you are resilient, and you are so deeply loved. Never forget that you carry a light the world needs.\n\nFill these pages with your thoughts, your dreams, your quiet truths. They belong to you — and so does every ounce of love and support I have to give.'
    },
    son: {
      front: 'For My Special Son',
      inside: 'This journal is yours — a place to think, to write, and to make sense of the world in your own way.\n\nSome days will be easy. Some will be hard. On the harder ones, I hope these pages remind you of who you are: someone brave, someone kind, someone loved more than you will ever fully know.\n\nWrite your story here. Every feeling, every hope, every quiet question. It matters. You matter. And you are never walking through any of it alone.'
    },
    granddaughter: {
      front: 'For My Cherished Granddaughter',
      inside: 'This journal is a small piece of quiet, just for you. A place where your thoughts can rest, your heart can speak, and your story can slowly unfold.\n\nWrite the happy days and the heavy ones. Write your hopes, your doubts, the things you are still figuring out. All of it belongs here.\n\nAnd know this, always — you are cherished beyond words. These pages are yours. My love, my support, and my pride in you are yours too. Forever.'
    },
    grandson: {
      front: 'For My Cherished Grandson',
      inside: 'This journal is yours — a quiet space for thoughts that are still becoming, dreams that are still growing, and feelings that sometimes need a soft place to land.\n\nWrite freely. Write honestly. Nothing you put on these pages is too small, too messy, or too much.\n\nWhenever life feels uncertain, come back here. And remember — you are loved, you are supported, and you are believed in, every single day.'
    },
    mum: {
      front: 'For My Wonderful Mum',
      inside: 'This journal is a little space for you — after all the years you spent holding space for everyone else.\n\nWrite whatever you need to. The memories, the reflections, the words that never quite made it out loud. Let these pages be gentle with you, the way you have always been gentle with us.\n\nYou are loved. You are appreciated. And you deserve this quiet, this softness, this time — today and always.'
    },
    dad: {
      front: 'For My Wonderful Dad',
      inside: 'This journal is for the quiet moments — the ones that do not always get spoken about. A place for reflection, for remembering, for writing down the things that sit in your heart.\n\nYou have carried so much, so steadily, for so long. Let these pages be something just for you.\n\nAnd however you fill them — or even if you simply hold this as a reminder — know that you are loved, respected, and deeply appreciated. Always.'
    },
    neutral: {
      front: 'For Someone I Love',
      inside: 'This journal is a gift — a quiet place for your thoughts, your reflections, and the things that sit in your heart.\n\nSome things are easier to write than to say. These pages hold space for all of it — the big feelings, the quiet ones, and everything in between.\n\nMay this journal remind you that your words matter, your feelings matter, and you are always, always loved and supported.'
    },
  },
};

const OPTIONS = [
  { id: 'daughter', label: 'Beautiful Daughter' },
  { id: 'son', label: 'Special Son' },
  { id: 'granddaughter', label: 'Cherished Granddaughter' },
  { id: 'grandson', label: 'Cherished Grandson' },
  { id: 'mum', label: 'Wonderful Mum' },
  { id: 'dad', label: 'Wonderful Dad' },
  { id: 'neutral', label: 'Someone I love' },
  { id: 'own', label: 'Personalised' },
];

const MessagePreview = ({ productType = 'letters', productId = 'default', showAnimation = true, onSelect, colourChoice = null }) => {
  const [selected, setSelected] = useState('neutral');
  const [ownMessage, setOwnMessage] = useState('');

  const handleSelect = (id) => {
    setSelected(id);
    if (onSelect) {
      const label = OPTIONS.find(o => o.id === id)?.label || id;
      onSelect(label);
    }
  };

  const messages = MESSAGES[productType] || MESSAGES.letters;
  const currentMessage = selected !== 'own' ? messages[selected] : null;

  // Midnight Blue theme styling
  const isMidnight = colourChoice === 'Midnight Blue';
  const previewBox = isMidnight
    ? {
        background: 'linear-gradient(180deg, #0d1f2d 0%, #0a1924 100%)',
        border: '1px solid rgba(198,169,107,0.22)',
        shadow: '0 2px 20px rgba(6,16,23,0.35)',
      }
    : {
        background: '#FAF8F5',
        border: '1px solid rgba(198,169,107,0.08)',
        shadow: '0 2px 16px rgba(11,31,58,0.04)',
      };
  const labelClass = isMidnight ? 'text-[#C6A96B]/70' : 'text-[#C6A96B]/50';
  const frontClass = isMidnight ? 'text-[#E8D5A8]' : 'text-[#0B1F3A]';
  const insideClass = isMidnight ? 'text-[#D4C9B0]' : 'text-[#6B6259]';
  const dividerClass = isMidnight ? 'border-[#C6A96B]/25' : 'border-[#C6A96B]/8';
  const ownTextClass = isMidnight ? 'text-[#E8D5A8]' : 'text-[#0B1F3A]';
  const ownPlaceholder = isMidnight ? 'placeholder:text-[#C6A96B]/40' : 'placeholder:text-[#6B6259]/55';
  const ownHelperClass = isMidnight ? 'text-[#C6A96B]/55' : 'text-[#6B6259]/75';

  return (
    <div data-testid={`message-preview-${productId}`}>
      {/* Selector */}
      <p className="text-xs tracking-[0.2em] uppercase text-[#C6A96B] mb-4 font-medium">Choose Your Message</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => handleSelect(opt.id)}
            data-testid={`msg-${productId}-${opt.id}`}
            className={`text-xs tracking-wide px-4 py-2.5 border transition-all duration-300 rounded-sm ${
              selected === opt.id
                ? 'border-[#C6A96B] bg-[#C6A96B]/10 text-[#0B1F3A] font-medium'
                : 'border-[#C6A96B]/12 text-[#6B6259] hover:border-[#C6A96B]/35'
            }`}
          >
            {selected === opt.id && <Check size={11} className="inline mr-1.5 -mt-0.5" strokeWidth={2.5} />}
            {opt.label}
          </button>
        ))}
      </div>

      {/* Preview Box */}
      <AnimatePresence mode="wait">
        {selected === 'own' ? (
          <motion.div
            key="own"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-lg p-6 sm:p-8"
            style={{
              background: previewBox.background,
              border: previewBox.border,
              boxShadow: previewBox.shadow,
            }}
            data-testid={`preview-own-${productId}`}
          >
            <p className={`text-xs tracking-[0.15em] uppercase mb-4 font-medium ${labelClass}`}>Your Message</p>
            <textarea
              value={ownMessage}
              onChange={(e) => setOwnMessage(e.target.value)}
              placeholder="Your message will appear here..."
              rows={5}
              data-testid={`own-textarea-${productId}`}
              className={`w-full bg-transparent border-0 text-base leading-[1.85] ${ownTextClass} font-heading italic ${ownPlaceholder} placeholder:not-italic resize-none outline-none`}
            />
            <p
              className={`mt-3 text-xs sm:text-[13px] leading-[1.7] italic ${ownHelperClass}`}
              data-testid={`own-helper-${productId}`}
            >
              You&rsquo;re welcome to type your message here, or leave it blank if you&rsquo;d prefer to handwrite something personal later.
            </p>
          </motion.div>
        ) : currentMessage ? (
          <motion.div
            key={`${selected}-${isMidnight ? 'mid' : 'cream'}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-lg overflow-hidden"
            style={{
              background: previewBox.background,
              border: previewBox.border,
              boxShadow: previewBox.shadow,
            }}
            data-testid={`preview-box-${productId}`}
          >
            {/* Front */}
            <div className={`px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b ${dividerClass}`}>
              <p className={`text-xs tracking-[0.15em] uppercase mb-2 font-medium ${labelClass}`}>Front</p>
              <p className={`text-lg sm:text-xl font-heading font-light leading-[1.4] ${frontClass}`} data-testid={`preview-front-${productId}`}>
                {currentMessage.front}
              </p>
            </div>

            {/* Inside */}
            <div className="px-6 sm:px-8 pt-4 pb-6 sm:pb-8 max-h-[280px] overflow-y-auto">
              <p className={`text-xs tracking-[0.15em] uppercase mb-3 font-medium ${labelClass}`}>Inside</p>
              {currentMessage.inside.split('\n\n').map((paragraph, i) => (
                <p
                  key={i}
                  className={`text-sm sm:text-base leading-[1.85] font-heading italic mb-3 last:mb-0 ${insideClass}`}
                  data-testid={`preview-inside-${productId}-p${i}`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Gift Visual Mockup */}
      {showAnimation && selected !== 'own' && currentMessage && (
        <GiftPreview
          frontText={currentMessage.front}
          insideText={currentMessage.inside}
          productType={productType}
          productId={productId}
        />
      )}
      {showAnimation && selected === 'own' && ownMessage.trim() && (
        <GiftPreview
          frontText="Your Personal Message"
          insideText={ownMessage}
          productType={productType}
          productId={productId}
        />
      )}
    </div>
  );
};

export { MessagePreview, MESSAGES, OPTIONS };
export default MessagePreview;
